var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import cloneDeep from 'lodash-es/cloneDeep';
import { alphaWithSpaces, isValidIban, isValidBicSwift, betweenLength, isValidAccountNumber } from 'utils/validators';
import { required, numeric, or } from 'vuelidate/lib/validators';
var Bank = /** @class */ (function () {
    function Bank(currency) {
        this.fields = {};
        this.validations = {};
        this.addressDetailsValidations = {
            accountHolderCountry: {
                required: required
            },
            accountHolderCity: {
                required: required,
                alphaWithSpaces: alphaWithSpaces
            },
            bankCountry: {
                required: required
            },
            bankCity: {
                required: required,
                alphaWithSpaces: alphaWithSpaces
            },
            correspondentBankCountry: {
                required: required
            },
            correspondentBankCity: {
                required: required,
                alphaWithSpaces: alphaWithSpaces
            }
        };
        this.defaultValidations = {
            accHolderName: {
                required: required,
                alphaWithSpaces: alphaWithSpaces
            },
            accNumber: {
                required: required,
                isValidNumberOrIban: or(isValidIban, isValidAccountNumber)
            },
            bankName: {
                required: required,
                alphaWithSpaces: alphaWithSpaces
            },
            currency: {
                required: required
            },
            bankNameCorresponded: {
                required: required,
                alphaWithSpaces: alphaWithSpaces
            },
            bankSwiftCorresponded: {
                required: required,
                isValidBicSwift: isValidBicSwift
            }
        };
        this.bankTransferValidations = __assign(__assign(__assign({}, this.addressDetailsValidations), this.defaultValidations), { bicSwift: {
                required: required,
                isValidBicSwift: isValidBicSwift
            } });
        this.bankTransferNoIBANValidations = __assign(__assign(__assign({}, this.defaultValidations), this.addressDetailsValidations), { accNumber: {
                required: required,
                isValidAccountNumber: isValidAccountNumber
            }, swift: {
                required: required,
                isValidBicSwift: isValidBicSwift
            } });
        this.bankTransferEUValidations = {
            accHolderName: {
                required: required,
                alphaWithSpaces: alphaWithSpaces
            },
            accNumber: {
                isValidIban: isValidIban,
                requiredIban: required
            }
        };
        this.bankTransferUkraineValidations = __assign(__assign(__assign({}, this.defaultValidations), this.addressDetailsValidations), { accHolderName: {
                required: required,
                alphaWithSpaces: alphaWithSpaces
            }, swift: {
                required: required,
                isValidBicSwift: isValidBicSwift
            } });
        this.bankTransferUSAValidations = __assign(__assign(__assign({}, this.defaultValidations), this.addressDetailsValidations), { accNumber: {
                requiredAccNumber: required,
                between: betweenLength(5, 17),
                numeric: numeric
            }, routingNumber: {
                required: required
            }, swift: {
                required: required,
                isValidBicSwift: isValidBicSwift
            } });
        this.bankTransferCanadaValidations = __assign(__assign(__assign({}, this.bankTransferUSAValidations), this.addressDetailsValidations), { routingNumber: {
                required: required,
                length5or9: or(function (value) {
                    return value.length === 5;
                }, function (value) {
                    return value.length === 9;
                })
            }, accountHolderStreet: {
                required: required
            } });
        this.bankTransferRussiaValidations = {
            accHolderName: {
                required: required,
                alphaWithSpaces: alphaWithSpaces
            },
            accNumber: {
                required: required
            },
            accNumberCorresponded: {
                required: required
            },
            bankName: {
                required: required
            },
            bankNameCorresponded: {
                required: required
            },
            bik: {
                required: required
            },
            currency: {
                required: required
            },
            innCode: {
                required: required
            },
            kppCode: {
                required: required
            },
            swift: {
                required: required
            }
        };
        this.bankTransfer = {
            accHolderName: '',
            accNumber: '',
            bankName: '',
            bicSwift: '',
            currency: '',
            bankNameCorresponded: '',
            bankSwiftCorresponded: '',
            accountHolderCountry: {},
            accountHolderCity: '',
            bankCountry: {},
            bankCity: '',
            correspondentBankCountry: {},
            correspondentBankCity: ''
        };
        this.bankTransferEU = {
            accHolderName: '',
            accNumber: '',
            currency: 'EUR'
        };
        this.bankTransferUkraine = {
            accHolderName: '',
            accNumber: '',
            bankName: '',
            currency: '',
            swift: '',
            bankNameCorresponded: '',
            bankSwiftCorresponded: '',
            accountHolderCountry: {},
            accountHolderCity: '',
            accountHolderStreet: '',
            bankCountry: {},
            bankCity: '',
            correspondentBankCountry: {},
            correspondentBankCity: ''
        };
        this.bankTransferUSA = {
            accHolderName: '',
            accNumber: '',
            bankName: '',
            currency: 'USD',
            routingNumber: '',
            swift: '',
            bankNameCorresponded: '',
            bankSwiftCorresponded: '',
            accountHolderCountry: {},
            accountHolderCity: '',
            bankCountry: {},
            bankCity: '',
            correspondentBankCountry: {},
            correspondentBankCity: ''
        };
        this.bankTransferCanada = {
            accHolderName: '',
            accNumber: '',
            bankName: '',
            currency: 'CAD',
            routingNumber: '',
            swift: '',
            bankNameCorresponded: '',
            bankSwiftCorresponded: '',
            accountHolderCountry: {},
            accountHolderCity: '',
            accountHolderStreet: '',
            bankCountry: {},
            bankCity: '',
            correspondentBankCountry: {},
            correspondentBankCity: ''
        };
        this.bankTransferRussia = {
            accHolderName: '',
            accNumber: '',
            accNumberCorresponded: '',
            bankName: '',
            bankNameCorresponded: '',
            bik: '',
            currency: '',
            innCode: '',
            kppCode: '',
            swift: ''
        };
        this.bankTransferNoIBAN = {
            accHolderName: '',
            accNumber: '',
            bankName: '',
            swift: '',
            currency: '',
            bankNameCorresponded: '',
            bankSwiftCorresponded: '',
            accountHolderCountry: {},
            accountHolderCity: '',
            bankCountry: {},
            bankCity: '',
            correspondentBankCountry: {},
            correspondentBankCity: ''
        };
        this.errorMsg = {
            accHolderName: {
                required: 'reusable.bankInfo.accHolderName.validation.missing',
                alphaWithSpaces: 'reusable.bankInfo.generic.validation.alphaWithSpaces'
            },
            accNumber: {
                between: 'reusable.bankInfo.accNumber.validation.between',
                numeric: 'reusable.bankInfo.accNumber.validation.onlyNumbers',
                isValidIban: 'reusable.bankInfo.accIban.validation.invalid',
                required: 'reusable.bankInfo.accNumberOrIban.validation.missing',
                requiredIban: 'reusable.bankInfo.accIban.validation.missing',
                requiredAccNumber: 'reusable.bankInfo.accNumber.validation.missing',
                isValidNumberOrIban: 'reusable.bankInfo.accNumberOrIban.validation.invalid',
                isValidAccountNumber: 'reusable.bankInfo.accNumber.validation.invalid'
            },
            accNumberCorresponded: {
                required: 'reusable.bankInfo.accNumberCorresponded.validation.missing'
            },
            address: {
                required: 'reusable.bankInfo.address.validation.missing',
                alphaWithSpaces: 'reusable.bankInfo.generic.validation.alphaWithSpaces',
                alphaNumWithSpaces: 'reusable.bankInfo.generic.validation.alphaNumWithSpaces'
            },
            bankName: {
                required: 'reusable.bankInfo.bankName.validation.missing',
                alphaWithSpaces: 'reusable.bankInfo.generic.validation.alphaWithSpaces'
            },
            bankNameCorresponded: {
                required: 'reusable.bankInfo.bankNameCorresponded.validation.missing',
                alphaWithSpaces: 'reusable.bankInfo.generic.validation.alphaWithSpaces'
            },
            bankSwiftCorresponded: {
                required: 'reusable.bankInfo.bankSwiftCorresponded.validation.missing',
                isValidBicSwift: 'reusable.bankInfo.bankSwiftCorresponded.validation.incorrect'
            },
            bicSwift: {
                required: 'reusable.bankInfo.bicSwift.validation.missing',
                isValidBicSwift: 'reusable.bankInfo.bicSwift.validation.incorrect'
            },
            bik: {
                required: 'reusable.bankInfo.bik.validation.missing'
            },
            currency: {
                required: 'reusable.bankInfo.currency.validation.missing'
            },
            innCode: {
                required: 'reusable.bankInfo.innCode.validation.missing'
            },
            kppCode: {
                required: 'reusable.bankInfo.kppCode.validation.missing'
            },
            paymentMethod: {
                required: 'reusable.bankInfo.paymentMethod.validation.missing'
            },
            reimbursement: {
                required: 'reusable.bankInfo.reimbursement.validation.missing'
            },
            routingNumber: {
                alphaNumWithSpaces: 'reusable.bankInfo.routingNumber.validation.alphaNumWithSpaces',
                required: 'reusable.bankInfo.routingNumber.validation.missing',
                between: 'reusable.bankInfo.routingNumber.validation.between',
                length5or9: 'reusable.bankInfo.routingNumber.validation.length5or9'
            },
            swift: {
                required: 'reusable.bankInfo.swift.validation.missing',
                isValidBicSwift: 'reusable.bankInfo.swift.validation.incorrect'
            },
            totalAmount: {
                decimal: 'reusable.bankInfo.totalAmount.validation.numeric',
                required: 'reusable.bankInfo.totalAmount.validation.missing',
                maxValue: 'reusable.bankInfo.totalAmount.validation.maxValue'
            },
            reimbursementTypes: {
                isTrueAtLeastOne: 'reusable.bankInfo.reimbursement.checkbox.validation.missing'
            },
            accountHolderCountry: {
                required: 'reusable.bankInfo.accountHolderCountry.validation.missing',
                alphaWithSpaces: 'reusable.bankInfo.generic.validation.alphaWithSpaces'
            },
            accountHolderCity: {
                required: 'reusable.bankInfo.accountHolderCity.validation.missing',
                alphaWithSpaces: 'reusable.bankInfo.generic.validation.alphaWithSpaces'
            },
            bankCountry: {
                required: 'reusable.bankInfo.bankCountry.validation.missing',
                alphaWithSpaces: 'reusable.bankInfo.generic.validation.alphaWithSpaces'
            },
            bankCity: {
                required: 'reusable.bankInfo.bankCity.validation.missing',
                alphaWithSpaces: 'reusable.bankInfo.generic.validation.alphaWithSpaces'
            },
            correspondentBankCountry: {
                required: 'reusable.bankInfo.correspondentBankCountry.validation.missing',
                alphaWithSpaces: 'reusable.bankInfo.generic.validation.alphaWithSpaces'
            },
            correspondentBankCity: {
                required: 'reusable.bankInfo.correspondentBankCity.validation.missing',
                alphaWithSpaces: 'reusable.bankInfo.generic.validation.alphaWithSpaces'
            }
        };
        this._globalCurrency = '';
        if (typeof currency !== 'undefined') {
            this.globalCurrency = currency;
        }
    }
    Bank.prototype.getFields = function (type) {
        var filtered = cloneDeep(this.fields);
        switch (type) {
            case 'bankTransfer': {
                filtered = this.bankTransfer;
                break;
            }
            case 'bankTransferEU': {
                filtered = this.bankTransferEU;
                break;
            }
            case 'bankTransferRussia': {
                filtered = this.bankTransferRussia;
                break;
            }
            case 'bankTransferUkraine': {
                filtered = this.bankTransferUkraine;
                break;
            }
            case 'bankTransferUSA': {
                filtered = this.bankTransferUSA;
                break;
            }
            case 'bankTransferCanada': {
                filtered = this.bankTransferCanada;
                break;
            }
            case 'bankTransferNoIBAN': {
                filtered = this.bankTransferNoIBAN;
                break;
            }
        }
        return filtered;
    };
    Bank.prototype.getValidations = function (type) {
        var validations = cloneDeep(this.validations);
        switch (type) {
            case 'bankTransfer': {
                validations = this.bankTransferValidations;
                break;
            }
            case 'bankTransferNoIBAN': {
                validations = this.bankTransferNoIBANValidations;
                break;
            }
            case 'bankTransferEU': {
                validations = this.bankTransferEUValidations;
                break;
            }
            case 'bankTransferRussia': {
                validations = this.bankTransferRussiaValidations;
                break;
            }
            case 'bankTransferUkraine': {
                validations = this.bankTransferUkraineValidations;
                break;
            }
            case 'bankTransferUSA': {
                validations = this.bankTransferUSAValidations;
                break;
            }
            case 'bankTransferCanada': {
                validations = this.bankTransferCanadaValidations;
                break;
            }
        }
        return validations;
    };
    Object.defineProperty(Bank.prototype, "globalCurrency", {
        get: function () {
            return this._globalCurrency;
        },
        set: function (value) {
            this._globalCurrency = value;
        },
        enumerable: false,
        configurable: true
    });
    return Bank;
}());
export default Bank;
